/* The inspector pane's own chrome: the scroller, the column its sections stack in, and the card
 * that stands where the week and the muscle split do when their read fails.
 *
 * The pane's BOX — the background, the hairline, the radius, the shadow and `overflow-y: auto` —
 * is `PlanLibraryScreen.module.css`'s `.card` + `.inspector`, applied from the screen and
 * deliberately not restated here. The two panes are cut from one card and one file declares it;
 * a second copy of those six declarations is the near-duplicate the value discipline exists to
 * prevent, and it would also be the CSS-Modules chunk-order trap — a duplicated declaration wins
 * in dev and loses in the build.
 *
 * ── The 19px inset lives on the SECTION, not on the pane ─────────────────────────────────────
 * The hero is full-bleed: its photograph reaches all four edges of the card. So the pane has no
 * padding and each section below the hero carries `padding: 0 19px` of its own, which is exactly
 * how the specimen's `paneRightD` is built.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

/* The canon's helmet sizes its own scrollbar to eight, and Chromium's default is fifteen. The bar
 * is `shared/ui/scrollbar`, which this element also carries; this is only its size. */
.pane {
  --sb-size: 8px;
  --sb-radius: var(--radius-5px);
}

/* The column the sections stack in, and the element the swap animates. `flex: none` so a pane
 * taller than its content leaves the surplus at the foot rather than stretching the strip. */
.content {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-4);
  min-height: 0;
}

/* The pattern week's inset. The section itself is `entities/plan-draft`'s `PatternWeek`, which
 * declares no padding of its own since its promotion: the create dialog stands it flush. */
.week {
  padding: var(--space-0) var(--space-19px);
}

/* ── The two sections' read failed ──────────────────────────────────────────────────────────── */

/* One card where the week and the muscle split stand, at exactly their combined height — the two
 * reserves plus the gap between them. So a successful retry swaps ink for ink and moves nothing
 * below, which on this pane is the assigned-clients strip. */
.failure {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  align-items: center;
  justify-content: center;
  height: calc(var(--h-pl-week) + var(--space-4) + var(--h-pl-muscle));
  margin: var(--space-0) var(--space-19px);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-9-5px);
}

.failureText {
  margin: var(--space-0);
  font-size: var(--fs-pl-body);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
}

.retry {
  padding: var(--space-6px) var(--space-13px);
  font-size: var(--fs-pl-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-7px);
  cursor: pointer;
}

.retry:hover {
  background: var(--bg-chip);
}

.retry:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

/* ── Nothing is selected, because there is nothing to select ─────────────────────────────────── */

/* The card keeps its geometry: the state stands in the room the hero and the two sections would
 * have taken, so switching between "you have no plans" and a library that has just loaded does not
 * resize the pane. */
.empty {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  height: calc(var(--h-pl-hero) + var(--space-4) + var(--h-pl-week) + var(--space-4) + var(--h-pl-muscle));
  padding: var(--space-0) var(--space-19px);
}

/* ── The pane standing: the wait, and the failure ─────────────────────────────────────────────
 * The same box as `.empty` — the two are one reserve — with the pane's own outline inside it
 * rather than a centred sentence, and room for the neutral mark to stand over that outline when
 * the read failed. See `InspectorEmpty`. */
.standing {
  position: relative;
  display: flex;
  flex: none;
  flex-direction: column;
  height: calc(var(--h-pl-hero) + var(--space-4) + var(--h-pl-week) + var(--space-4) + var(--h-pl-muscle));
}

/* Centred over the whole outline, at the size and in the ink `EmptyState` draws its own mark: this
 * is the same drawing standing in a different arrangement, not a second treatment. */
.standingMark {
  position: absolute;
  inset: var(--space-0);
  display: grid;
  place-items: center;
  color: var(--text-3);
}

.standingMark > svg {
  display: block;
  width: var(--sz-empty-mark);
  height: var(--sz-empty-mark);
}

/* `.empty` centres its child, so the mark inside it needs no absolute box of its own. */
.empty .standingMark {
  position: static;
}

.shape {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-4);
}

/* The cover plate. Full-bleed, exactly as the hero is: its photograph reaches all four edges. */
.shapeHero {
  position: relative;
  flex: none;
  height: var(--h-pl-hero);
  border-radius: var(--space-0);
}

/* The two figures, where the plate prints them: bottom left, inside the section's own inset. */
.shapeKpis {
  position: absolute;
  bottom: var(--space-4);
  left: var(--space-19px);
  display: flex;
  gap: var(--space-4);
}

/* A plain fill rather than a bar: a bar inside a bar runs a second sweep over the first one's. */
.shapeKpi {
  width: 44px;
  height: 26px;
  background: var(--skeleton-sheen);
  border-radius: var(--radius-bar);
}

.shapeWeek {
  display: flex;
  flex: none;
  gap: var(--space-1);
  align-items: flex-end;
  height: var(--h-pl-week);
  padding: var(--space-0) var(--space-19px);
}

.shapeTile {
  flex: 1;
  height: var(--h-pl-tile);
}

.shapeMuscle {
  display: flex;
  flex: none;
  align-items: center;
  height: var(--h-pl-muscle);
  padding: var(--space-0) var(--space-19px);
}

.shapeBand {
  flex: 1;
  height: 18px;
}
